import { Injectable } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { CategoryType, UserCustomization } from '@flocasts/user-preferences-types'
import { combineLatest, Observable, of } from 'rxjs'
import { distinctUntilChanged, map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { UserPreferenceDataService } from 'src/app/singleton-services/user-preferences/user-preference-data.service'
import { VerticalService } from 'src/app/singleton-services/vertical.service'
import { CollectionEntityType } from 'src/io/models/collection'
import { CollectionCtaTypes } from './collection-cta.interfaces'
import { Category } from '@flocasts/flosports30-types/dist/entity'
import { collectionFavoritable } from './collection-favorite.utility'

@Injectable({
  providedIn: 'root'
})
export class CollectionFavoriteService {
  constructor(
    private readonly vs: VerticalService,
    private readonly userPrefService: UserPreferenceDataService,
    private readonly ar: ActivatedRoute
  ) {}

  /**
   * Gets the CTA for collection header and footer
   * Sign Up, Favorite, or Favorited
   */
  public getCtaType = (
    isUserPremium: boolean,
    isUserLoggedIn: boolean,
    collectionType: CollectionEntityType | 'contentcategory',
    coreNodeId: number,
    categories?: readonly Category[]
  ): Observable<CollectionCtaTypes | undefined> =>
    combineLatest([this.vs.isFavoriteFeatureVertical$, this.queryNodeId$, this.vs.siteId$]).pipe(
      switchMap(([isFavoriteVertical, queryNodeId, siteId]) => {
        // Here we filter out which collection types are favoritable. The isFavoriteFeatureVertical$ already
        // segregates out verticals that shouldn't have this functionality no matter the collection type. We still need
        // the siteId since Floracing required additional filtering that should not be available to other favoritable verticals.
        // There is a TO DO to move the logic of VerticalService.IsFavoriteFeatureVertical$ out into a utility funciton and
        // consolidate some of this functionality and reduce duplication.
        const collectionCanBeFavorited = collectionFavoritable(
          isFavoriteVertical,
          siteId,
          collectionType,
          categories,
          coreNodeId
        )

        // If user is not authenticated
        if (!isUserLoggedIn) {
          return collectionCanBeFavorited ? of(CollectionCtaTypes.FAVORITE) : of(CollectionCtaTypes.SIGN_UP)
        }

        // if user is authenticated
        if (collectionCanBeFavorited) {
          if (!queryNodeId) {
            return this.userPrefService.getUserCustomizationByCoreNodeId(coreNodeId, collectionType, true).pipe(
              map(favorite => (!!favorite ? CollectionCtaTypes.FAVORITED : CollectionCtaTypes.FAVORITE)),
              distinctUntilChanged(),
              shareReplay(1)
            )
          }
          return this.userPrefService
            .postUserCustomizationByCoreNodeId(queryNodeId, collectionType)
            .pipe(map(favorite => (!!favorite ? CollectionCtaTypes.FAVORITED : CollectionCtaTypes.FAVORITE)))
        }

        return isUserPremium ? of(undefined) : of(CollectionCtaTypes.SIGN_UP)
      }),
      distinctUntilChanged(),
      shareReplay({
        bufferSize: 1,
        refCount: true
      })
    )

  public favoriteByCollectionIdAndType = (
    coreNodeId: number,
    collectionType: CollectionEntityType | CategoryType
  ): Observable<UserCustomization | undefined> => {
    return this.userPrefService.postUserCustomizationByCoreNodeId(coreNodeId, collectionType)
  }

  public unfavorite = (
    coreNodeId: number,
    collectionType: CollectionEntityType | CategoryType
  ): Observable<void | undefined> => {
    return this.userPrefService.getUserCustomizationByCoreNodeId(coreNodeId, collectionType, true).pipe(
      switchMap(favorite => {
        return !!favorite ? this.userPrefService.deleteUserCustomization(favorite.id) : of(undefined)
      })
    )
  }

  private queryNodeId$ = this.ar.queryParamMap.pipe(
    map(params => (params.get('coreNodeId') ? Number(params.get('coreNodeId')) : undefined)),
    distinctUntilChanged(),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  )
}
